import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  AgentListRequest,
  AgentListResponse,
  AgentRouteRequest,
  AgentRouteResponse,
  AgentStats,
  DeviationListRequest,
  DeviationListResponse,
  FieldSupervisorDashboardState,
  IFailedPayload,
} from 'redux/types/FieldSupervisorDashboard.types';

// Using the FieldSupervisorDashboardState from types file

export const fieldSupervisorDashboardInitState: FieldSupervisorDashboardState =
  {
    loading: false,
    onlineAgentListLoading: false,
    error: null,
    onlineAgentError: null,
    agentList: null,
    agentStats: null,
    agentStatsLoading: false,
    onlineAgentList: null,
    deviationList: null,
    agentRoute: null,
    agentRouteLoading: false,
    agentRouteError: null,
  };

const FieldSupervisorDashboardSlice = createSlice({
  name: 'fieldSupervisorDashboard',
  initialState: fieldSupervisorDashboardInitState,
  reducers: {
    // Fetch agent list
    fetchAgentList: (state, action: PayloadAction<AgentListRequest>) => {
      state.loading = true;
      state.error = null;
    },
    fetchAgentListSuccess: (
      state,
      action: PayloadAction<AgentListResponse>
    ) => {
      state.loading = false;
      state.agentList = action.payload;
    },
    fetchAgentListFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Fetch agent stats (always with statusFilter='ALL')
    fetchAgentStats: (state, action: PayloadAction<AgentListRequest>) => {
      state.agentStatsLoading = true;
    },
    fetchAgentStatsSuccess: (state, action: PayloadAction<AgentStats>) => {
      state.agentStatsLoading = false;
      state.agentStats = action.payload;
    },
    fetchAgentStatsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.agentStatsLoading = false;
    },
    // Fetch deviation list
    fetchDeviationList: (
      state,
      action: PayloadAction<DeviationListRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchDeviationListSuccess: (
      state,
      action: PayloadAction<DeviationListResponse>
    ) => {
      state.loading = false;
      state.deviationList = action.payload;
    },
    fetchDeviationListFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Fetch ONLINE agent list (for map)
    fetchOnlineAgentList: (state, action: PayloadAction<AgentListRequest>) => {
      state.onlineAgentListLoading = true;
      state.onlineAgentError = null;
    },
    fetchOnlineAgentListSuccess: (
      state,
      action: PayloadAction<AgentListResponse>
    ) => {
      state.onlineAgentListLoading = false;
      state.onlineAgentList = action.payload;
      // Stats (totalAgentCount, onlineAgentCount, offlineAgentCount) should only come from
      // fetchAgentList with statusFilter='ALL', so we don't update them here
    },
    fetchOnlineAgentListFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.onlineAgentListLoading = false;
      state.onlineAgentError = action.payload;
    },
    // Fetch agent route
    fetchAgentRoute: (state, action: PayloadAction<AgentRouteRequest>) => {
      state.agentRouteLoading = true;
      state.agentRouteError = null;
    },
    fetchAgentRouteSuccess: (
      state,
      action: PayloadAction<AgentRouteResponse>
    ) => {
      state.agentRouteLoading = false;
      state.agentRoute = action.payload;
    },
    fetchAgentRouteNoDataSuccess: (state) => {
      state.agentRouteLoading = false;
      state.agentRoute = null;
    },
    fetchAgentRouteFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.agentRouteLoading = false;
      state.agentRouteError = action.payload;
      state.agentRoute = null;
    },
  },
});

export const {
  fetchAgentList,
  fetchAgentListSuccess,
  fetchAgentListFailure,
  fetchAgentStats,
  fetchAgentStatsSuccess,
  fetchAgentStatsFailure,
  fetchDeviationList,
  fetchDeviationListSuccess,
  fetchDeviationListFailure,
  fetchOnlineAgentList,
  fetchOnlineAgentListSuccess,
  fetchOnlineAgentListFailure,
  fetchAgentRoute,
  fetchAgentRouteSuccess,
  fetchAgentRouteNoDataSuccess,
  fetchAgentRouteFailure,
} = FieldSupervisorDashboardSlice.actions;

export default FieldSupervisorDashboardSlice.reducer;
